import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { toast } from "react-toastify";
import {
  useGetArtistArtworkQuery,
  useUpdateArtistArtworkMutation,
} from "../../services/rtkApi/artistsGalleryApi";
import { useAppSelector } from "../../store/hooks";
import ArtworkForm, { ArtworkFormValues } from "./ArtworkForm";
import { ArtistsGalleryFontLinks } from "./ArtistsGalleryFontLinks";
import { artistsGalleryFontFamily } from "./artistsGalleryFont";

const EditArtistArtworkPage: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const bootstrapStatus = useAppSelector((s) => s.auth.bootstrapStatus);
  const ready = bootstrapStatus === "ready";

  const { data: artwork, isLoading, error } = useGetArtistArtworkQuery(id, {
    skip: !id || !ready,
  });
  const [updateArtistArtwork, { isLoading: isSubmitting }] =
    useUpdateArtistArtworkMutation();

  const handleSubmit = async (values: ArtworkFormValues) => {
    const fd = new FormData();
    fd.append("title", values.title);
    fd.append("description", values.description);
    if (values.dateMade) {
      fd.append("dateMade", new Date(values.dateMade).toISOString());
    }
    fd.append("donation", String(values.donation));
    if (values.image) fd.append("image", values.image);
    try {
      await updateArtistArtwork({ id, formData: fd }).unwrap();
      const username = artwork?.artist?.username;
      navigate(username ? `/art/${encodeURIComponent(username)}` : "/art");
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to update artwork", {
        position: "bottom-right",
      });
    }
  };

  return (
    <Page>
      <ArtistsGalleryFontLinks />
      <Card>
        <Heading>edit artwork</Heading>

        {!ready && <Notice>resolving session…</Notice>}
        {ready && isLoading && <Notice>loading…</Notice>}
        {ready && error && (
          <Gate>
            could not load this artwork. it may not exist, or you may not have
            access to edit it.
          </Gate>
        )}
        {artwork && (
          <>
            <Banner>
              editing as a contributor will set the piece back to{" "}
              <strong>pending review</strong>.
            </Banner>
            <ArtworkForm
              initial={artwork}
              submitLabel="save"
              isSubmitting={isSubmitting}
              onSubmit={handleSubmit}
              onCancel={() => navigate(-1)}
            />
          </>
        )}
      </Card>
    </Page>
  );
};

const Page = styled.div`
  background-color: #c4ffd2;
  padding: 40px;
  min-height: 100vh;
  font-family: ${artistsGalleryFontFamily};

  @media (max-width: 600px) {
    padding: 20px;
  }
`;

const Card = styled.div`
  background: #ffffff;
  border: 4px solid #000000;
  box-shadow: 5px 10px rgba(0, 0, 0, 0.7);
  padding: 24px;
  max-width: 640px;
`;

const Heading = styled.h2`
  margin: 0 0 1rem 0;
  font-weight: 700;
`;

const Notice = styled.div`
  background: #fff77a;
  border: 3px solid #000000;
  box-shadow: 3px 4px rgba(0, 0, 0, 0.7);
  padding: 8px 12px;
  margin-bottom: 16px;
  font-weight: 600;
  font-size: 0.9rem;
`;

const Banner = styled.div`
  background: #fff77a;
  border: 3px solid #000000;
  box-shadow: 3px 4px rgba(0, 0, 0, 0.7);
  padding: 8px 12px;
  margin-bottom: 16px;
  font-size: 0.85rem;
`;

const Gate = styled.div`
  background: #ffd1f0;
  border: 3px solid #000000;
  box-shadow: 3px 4px rgba(0, 0, 0, 0.7);
  padding: 12px 14px;
  margin-bottom: 16px;
  font-size: 0.9rem;
  line-height: 1.4;
`;

export default EditArtistArtworkPage;
